import { useState } from 'react';
import {
  ActionButton,
  Autocomplete,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  HairlineCard,
  Icons,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  TagChip,
  TextField,
  titleCellSx,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type { MunicipalInvitationSummary, MunicipalityOption } from '@munifocus/contracts-gov';
import { useCompanies } from '../../lib/administration-api';
import {
  useInvitationAction,
  useInviteMunicipalAdministrator,
  useMunicipalInvitations,
  useMunicipalities,
} from '../../lib/seats-api';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * Inviting a municipality's administrator — usually the clerk — to approve this
 * company's officer appointments there.
 *
 * A company may work in any number of municipalities at no extra charge,
 * including ones that do not subscribe to Munifocus. Inviting the clerk creates
 * their account (and the municipality's record, if it is not listed at all),
 * opens the contract for council's decision, and emails them a link to set a
 * password. The clerk then approves or declines each proposed officer, and may
 * add deputy or assistant clerks — at no fee, and never from the company's
 * enforcement seats.
 */
export function MunicipalInvitationsPanel() {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const actionsColumn = useRowActionsColumn();
  const { active } = useScope();
  const companies = useCompanies(active?.kind !== 'company');
  const companyId = active?.kind === 'company' ? active.id : (companies.data?.[0]?.id ?? null);

  const invitations = useMunicipalInvitations(companyId);
  const invite = useInviteMunicipalAdministrator(companyId ?? '');
  const act = useInvitationAction(companyId ?? '');

  const [open, setOpen] = useState(false);
  const municipalities = useMunicipalities(companyId, open);
  const [municipality, setMunicipality] = useState<MunicipalityOption | null>(null);
  const [notListed, setNotListed] = useState(false);
  const [newName, setNewName] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState<'en' | 'fr'>(
    i18n.language?.startsWith('fr') ? 'fr' : 'en',
  );

  const reset = () => {
    setOpen(false);
    setMunicipality(null);
    setNotListed(false);
    setNewName('');
    setEmail('');
    setDisplayName('');
    setTitle('');
  };

  const run = async (successKey: string, fn: () => Promise<unknown>): Promise<boolean> => {
    try {
      await fn();
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.invitations.failed');
      return false;
    }
  };

  if (!companyId) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.employees.noCompany')}
        </Typography>
      </HairlineCard>
    );
  }

  const ready =
    Boolean(email.trim()) &&
    Boolean(displayName.trim()) &&
    (notListed ? newName.trim().length >= 2 : Boolean(municipality));

  return (
    <Stack spacing={2}>
      <HairlineCard sx={{ p: 2.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.invitations.explainer')}
        </Typography>
      </HairlineCard>

      <DataTable
        rows={invitations.data ?? []}
        getRowKey={(row) => row.id}
        isLoading={invitations.isLoading}
        emptyLabel={t('gov.admin.invitations.empty')}
        initialSortKey="municipality"
        action={
          <ActionButton
            label={t('gov.admin.invitations.invite')}
            icon={Icons.Send}
            onClick={() => setOpen(true)}
          />
        }
        columns={[
          {
            key: 'municipality',
            label: t('gov.admin.invitations.municipality'),
            primary: true,
            sortValue: (row) => row.localGovernmentName,
            cell: (row) => row.localGovernmentName,
          },
          {
            key: 'person',
            label: t('gov.admin.invitations.person'),
            sortValue: (row) => row.displayName,
            cell: (row) => (
              <Stack>
                <Typography variant="body2" sx={titleCellSx}>
                  {row.displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.email}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'status',
            label: t('gov.admin.invitations.status'),
            sortValue: (row) => invitationState(row),
            cell: (row) => (
              <StatusChip
                status={
                  row.status === 'cancelled' ? 'neutral' : row.accepted ? 'success' : 'pending'
                }
                label={t(`gov.admin.invitations.states.${invitationState(row)}`)}
              />
            ),
          },
          {
            key: 'contract',
            label: t('gov.admin.invitations.contract'),
            sortValue: (row) => row.contractStatus ?? '',
            cell: (row) =>
              row.contractStatus ? (
                <TagChip label={t(`gov.admin.invitations.contractStates.${row.contractStatus}`)} />
              ) : (
                '—'
              ),
          },
          {
            key: 'sent',
            label: t('gov.admin.invitations.lastSent'),
            nowrap: true,
            sortValue: (row) => row.lastSentAt,
            cell: (row) =>
              frenchFirstOfMonth(new Date(row.lastSentAt).toLocaleDateString(i18n.language)),
          },
          {
            ...actionsColumn,
            cell: (row: MunicipalInvitationSummary) =>
              row.status === 'sent' ? (
                <RowActions>
                  {!row.accepted && (
                    <RowAction
                      label={t('gov.admin.invitations.resend')}
                      icon={Icons.Repeat}
                      onClick={() =>
                        run('gov.admin.invitations.resent', () =>
                          act.mutateAsync({ id: row.id, action: 'resend' }),
                        )
                      }
                    />
                  )}
                  <RowAction
                    label={t('gov.admin.invitations.cancel')}
                    icon={Icons.UserMinus}
                    tone="danger"
                    onClick={() =>
                      run('gov.admin.invitations.cancelled', () =>
                        act.mutateAsync({ id: row.id, action: 'cancel' }),
                      )
                    }
                  />
                </RowActions>
              ) : null,
          },
        ]}
      />

      <Dialog open={open} onClose={reset} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.admin.invitations.invite')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {notListed ? (
              <TextField
                label={t('gov.admin.invitations.newMunicipality')}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                fullWidth
                required
              />
            ) : (
              <Autocomplete
                options={(municipalities.data ?? []).filter((m) => m.accessTier !== 'full')}
                loading={municipalities.isLoading}
                value={municipality}
                onChange={(_, value) => setMunicipality(value)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('gov.admin.invitations.municipality')} required />
                )}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox checked={notListed} onChange={(e) => setNotListed(e.target.checked)} />
              }
              label={t('gov.admin.invitations.notListed')}
            />
            <TextField
              label={t('gov.admin.invitations.name')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('gov.admin.invitations.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              fullWidth
              required
            />
            <TextField
              label={t('gov.admin.invitations.title')}
              placeholder={t('gov.admin.invitations.titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
            <TextField
              select
              label={t('gov.admin.invitations.language')}
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'en' | 'fr')}
              fullWidth
            >
              <MenuItem value="en">{t('gov.admin.invitations.languages.en')}</MenuItem>
              <MenuItem value="fr">{t('gov.admin.invitations.languages.fr')}</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary">
              {t('gov.admin.invitations.help')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={reset}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!ready || invite.isPending}
            onClick={async () => {
              const ok = await run('gov.admin.invitations.sent', () =>
                invite.mutateAsync({
                  ...(notListed
                    ? { newLocalGovernmentName: newName.trim() }
                    : { localGovernmentId: municipality?.id }),
                  email: email.trim(),
                  displayName: displayName.trim(),
                  title: title.trim() || undefined,
                  language,
                }),
              );
              if (ok) reset();
            }}
          >
            {t('gov.admin.invitations.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function invitationState(row: MunicipalInvitationSummary): 'cancelled' | 'accepted' | 'sent' {
  if (row.status === 'cancelled') return 'cancelled';
  return row.accepted ? 'accepted' : 'sent';
}
